import type { ReactNode } from "react";
import { MenuItem, MenuList, Paper, Typography } from "@mui/material";
import { Stack } from "@mui/system";
import { Outlet, useLocation, useNavigate } from "react-router";
import { useTranslation } from "react-i18next";
import {
  EXTERNAL_REGISTER_ROUTE_METADATA,
  EXTERNAL_REGISTER_SETTINGS_NAV_ITEMS,
} from "./externalRegister/externalRegisterRoutes";

type SettingsNavItemProps = {
  to: string;
  children: ReactNode;
  nested?: boolean;
  active: boolean;
  onSelect: (to: string) => void;
};

const SettingsNavItem = ({ to, children, nested = false, active, onSelect }: SettingsNavItemProps) => (
  <MenuItem
    onClick={() => onSelect(to)}
    selected={active}
    sx={{ borderRadius: 0.5, ...(nested && { pl: 4, fontSize: "0.9rem" }) }}
  >
    {children}
  </MenuItem>
);

export const SettingsPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const selectRoute = (to: string) => navigate(to);
  const isActive = (to: string) => location.pathname === `/settings/${to}`;
  const isActivePrefix = (to: string) => location.pathname.startsWith(`/settings/${to}`);

  return (
    <Stack p={2} width="100vw" height="100%" overflow="hidden" sx={{ position: "absolute", top: 0, left: 0, right: 0, bottom: 0 }}>
      <Stack direction="row" alignItems="flex-start" minHeight={0} flex={1} width="100%">
        <Paper sx={{ width: 200, flexShrink: 0, borderRight: "1px solid #e5e5e5", boxShadow: "none", borderRadius: 0, height: "100%" }}>
          <MenuList sx={{ display: "flex", flexDirection: "column", gap: 1, pr: 1, pt: 2 }}>
            <Typography variant="h4">Settings</Typography>
            <SettingsNavItem to="locations" active={isActive("locations")} onSelect={selectRoute}>
              Locations
            </SettingsNavItem>
            <SettingsNavItem to="i18n" active={isActive("i18n")} onSelect={selectRoute}>
              Translations
            </SettingsNavItem>
            <SettingsNavItem to="faqs" active={isActive("faqs")} onSelect={selectRoute}>
              FAQs
            </SettingsNavItem>
            <SettingsNavItem to="encounters" active={isActive("encounters")} onSelect={selectRoute}>
              Encounters
            </SettingsNavItem>
            <SettingsNavItem to="task-definitions" nested active={isActive("task-definitions")} onSelect={selectRoute}>
              Task Definitions
            </SettingsNavItem>
            <SettingsNavItem to="visit-details" nested active={isActive("visit-details")} onSelect={selectRoute}>
              Visit Details
            </SettingsNavItem>
            <SettingsNavItem to="patient-details" active={isActive("patient-details")} onSelect={selectRoute}>
              Patient Details
            </SettingsNavItem>
            <SettingsNavItem to="complaint-codes" active={isActive("complaint-codes")} onSelect={selectRoute}>
              Complaint Codes
            </SettingsNavItem>
            <SettingsNavItem to="clinician-ids" active={isActive("clinician-ids")} onSelect={selectRoute}>
              Clinician IDs
            </SettingsNavItem>
            <SettingsNavItem to="scheduler" active={isActive("scheduler")} onSelect={selectRoute}>
              Scheduler
            </SettingsNavItem>
            <SettingsNavItem to="integration" active={isActive("integration")} onSelect={selectRoute}>
              Integration
            </SettingsNavItem>
            {EXTERNAL_REGISTER_SETTINGS_NAV_ITEMS.map((item) => (
              <SettingsNavItem
                key={item.key}
                to={item.relativePath}
                nested={item.nestedInSettingsNav}
                active={item.key === EXTERNAL_REGISTER_ROUTE_METADATA.root.key ? isActivePrefix(item.relativePath) : isActive(item.relativePath)}
                onSelect={selectRoute}
              >
                {t(item.labelKey)}
              </SettingsNavItem>
            ))}
          </MenuList>
        </Paper>
        <main style={{ display: "flex", flex: 1, height: "100%", width: "100%", minWidth: 0, maxWidth: "none" }}>
          <Outlet />
        </main>
      </Stack>
    </Stack>
  );
};
